import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface CockpitUser {
  id: string;
  name: string;
  email: string;
  createdOn: string; // format: 03 May 2026
  lastLoggedIn: string;
  status: 'Active' | 'Inactive';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2024-12-12T..." -> "12 Dec 2024"
const formatCreatedOn = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// "2026-06-19T08:32..." -> "19 Jun 2026, 08:32 AM" / "Never"
const formatLastLogin = (iso?: string | null): string => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Never';
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

const mapApiAdminToUser = (a: any): CockpitUser => ({
  id: String(a.id),
  name: a.name,
  email: a.email,
  createdOn: formatCreatedOn(a.created_at),
  lastLoggedIn: formatLastLogin(a.last_login),
  status: a.is_active === false ? 'Inactive' : 'Active',
});

export const CockpitAdminPanel: React.FC = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

  const [users, setUsers] = useState<CockpitUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Inactive'>('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CockpitUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Custom Toast notification states
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'info' | 'error'>('success');

  const triggerToast = (msg: string, type: 'success' | 'info' | 'error' = 'success') => {
    setToastMessage(msg);
    setToastType(type);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // Form states
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    status: 'Active' as 'Active' | 'Inactive'
  });

  const [copySuccess, setCopySuccess] = useState(false);

  const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAdmins = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await fetch(`${baseUrl}/api/v1/super-admins`, {
        headers: { ...authHeaders() },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch cockpit admins');
      setUsers((data.data || []).map(mapApiAdminToUser));
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load cockpit admins');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Copy Credentials inside Modal
  const handleCopyCredentials = () => {
    if (!newUser.email) {
      triggerToast("Please enter an Email ID before copying credentials.", "error");
      return;
    }
    const pwdValue = newUser.password || 'Temporary_Admin_Pass_2026';
    const textToCopy = `Cockpit Access Details:\nEmail ID: ${newUser.email}\nPassword: ${pwdValue}`;

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopySuccess(true);
      triggerToast("Account credentials copied to clipboard!", "success");
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(() => {
      triggerToast("Failed to copy automatically. Please copy manually.", "error");
    });
  };

  const handleEditClick = (user: CockpitUser) => {
    setSelectedUser(user);
    setNewUser({
      name: user.name,
      email: user.email,
      password: '',
      confirmPassword: '',
      status: user.status
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setSelectedUser(null);
    setNewUser({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      status: 'Active'
    });
    setShowAddModal(true);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.name.trim() || !newUser.email.trim()) {
      triggerToast("Verification failed: Full Name and Email are required.", "error");
      return;
    }

    // Password is required when creating; optional when editing (blank = keep current).
    if (!isEditing && !newUser.password) {
      triggerToast("A password is required for a new administrator.", "error");
      return;
    }

    if (newUser.password !== newUser.confirmPassword) {
      triggerToast("Passphrase mismatch: Confirm Password does not match Password.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditing && selectedUser) {
        // Edit Path — PUT /api/v1/super-admins/:id
        const body: Record<string, any> = {
          name: newUser.name.trim(),
          email: newUser.email.trim(),
          is_active: newUser.status === 'Active',
        };
        if (newUser.password) body.password = newUser.password;

        const response = await fetch(`${baseUrl}/api/v1/super-admins/${selectedUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || 'Failed to update admin');

        setUsers(prev => prev.map(u => (u.id === selectedUser.id ? mapApiAdminToUser(data.data) : u)));
        setShowAddModal(false);
        triggerToast(`Successfully modified admin profile for ${data.data.name}`, "success");
      } else {
        // Create Path — POST /api/v1/super-admins
        const response = await fetch(`${baseUrl}/api/v1/super-admins`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            name: newUser.name.trim(),
            email: newUser.email.trim(),
            password: newUser.password,
            is_active: newUser.status === 'Active',
          }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || 'Failed to create admin');

        setUsers(prev => [mapApiAdminToUser(data.data), ...prev]);
        setShowAddModal(false);
        triggerToast(`Created new administrator ${data.data.name} ready for sign-in`, "success");
      }

      // Reset newUser
      setNewUser({ name: '', email: '', password: '', confirmPassword: '', status: 'Active' });
    } catch (err: any) {
      triggerToast(err.message || 'Something went wrong. Please try again.', "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleUserStatus = async (user: CockpitUser) => {
    const nextStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    try {
      const response = await fetch(`${baseUrl}/api/v1/super-admins/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ is_active: nextStatus === 'Active' }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to update status');

      setUsers(prev => prev.map(u => (u.id === user.id ? mapApiAdminToUser(data.data) : u)));
      triggerToast(`Admin ${user.name} is now ${nextStatus}`, "info");
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update status', "error");
    }
  };

  const deleteUser = async (user: CockpitUser) => {
    if (!window.confirm(`Are you sure you want to remove ${user.name} from cockpit administrators?`)) return;
    try {
      const response = await fetch(`${baseUrl}/api/v1/super-admins/${user.id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to remove admin');

      setUsers(prev => prev.filter(u => u.id !== user.id));
      triggerToast(`Removed cockpit administrator: ${user.name}`, 'info');
    } catch (err: any) {
      triggerToast(err.message || 'Failed to remove admin', "error");
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || u.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="flex flex-col gap-8 animate-fade-in font-sans relative">
      {/* Toast Notification Container */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: 20 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -20, x: 20 }}
            className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3.5 rounded-xl border shadow-lg ${
              toastType === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
              toastType === 'info' ? 'bg-blue-50 text-blue-800 border-blue-200' :
              'bg-rose-50 text-rose-800 border-rose-200'
            }`}
          >
            <span className="material-symbols-outlined text-[20px]">
              {toastType === 'success' ? 'check_circle' :
               toastType === 'info' ? 'info' : 'warning'}
            </span>
            <span className="text-xs font-semibold">{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Cockpit Admin Management</h2>
          <p className="text-xs text-slate-500 mt-1">Manage core platform administrators, grant operational access, and audit master identities.</p>
        </div>

        <div>
          <button
            onClick={handleOpenAddModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/25"
          >
            <span className="material-symbols-outlined text-[16px]">person_add</span>
            Add New Admin
          </button>
        </div>
      </div>

      {/* KPI stats bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-all">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Total Admin Users</span>
            <span className="text-2xl font-extrabold text-slate-950 font-mono">{users.length}</span>
          </div>
          <div className="w-10 h-10 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600">
            <span className="material-symbols-outlined text-lg">supervisor_account</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-all">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Active Admins</span>
            <span className="text-2xl font-extrabold text-emerald-600 font-mono">
              {users.filter(u => u.status === 'Active').length}
            </span>
          </div>
          <div className="w-10 h-10 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-center text-emerald-600">
            <span className="material-symbols-outlined text-lg">verified_user</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-all">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Inactive / Pending</span>
            <span className="text-2xl font-extrabold text-[#f59e0b] font-mono">
              {users.filter(u => u.status === 'Inactive').length}
            </span>
          </div>
          <div className="w-10 h-10 bg-amber-50 rounded-xl border border-amber-100 flex items-center justify-center text-amber-600">
            <span className="material-symbols-outlined text-lg">no_accounts</span>
          </div>
        </div>
      </div>

      {/* Main Users Table Grid */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
        {/* Table Filters header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200/50">
              <button
                onClick={() => setStatusFilter('All')}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                  statusFilter === 'All'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                All States
              </button>
              <button
                onClick={() => setStatusFilter('Active')}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                  statusFilter === 'Active'
                    ? 'bg-white text-emerald-600 shadow-sm font-semibold'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setStatusFilter('Inactive')}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                  statusFilter === 'Inactive'
                    ? 'bg-white text-rose-600 shadow-sm font-semibold'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Inactive
              </button>
            </div>
          </div>

          <div className="relative max-w-xs w-full">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Cockpit users..."
              className="w-full bg-slate-50 text-xs rounded-xl border border-slate-200 pl-10 pr-4 py-2.5 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-[#136bce] focus:border-[#136bce] transition-all focus:outline-none"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[750px]">
            <thead>
              <tr className="border-b border-slate-200 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                <th className="py-4 px-4 font-extrabold">User Name</th>
                <th className="py-4 px-4 font-extrabold">Email ID</th>
                <th className="py-4 px-4 font-extrabold">Created On</th>
                <th className="py-4 px-4 font-extrabold">Last Logged in</th>
                <th className="py-4 px-4 font-extrabold">Status</th>
                <th className="py-4 px-4 text-right font-extrabold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400 text-xs">
                    <span className="material-symbols-outlined text-[28px] text-slate-300 block mb-2 animate-spin">progress_activity</span>
                    Loading cockpit administrators…
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-xs">
                    <span className="material-symbols-outlined text-[32px] text-rose-300 block mb-2">error</span>
                    <p className="text-rose-600 font-semibold mb-3">{loadError}</p>
                    <button
                      onClick={fetchAdmins}
                      className="px-4 py-1.5 bg-[#136bce] text-white rounded-lg text-[11px] font-bold hover:bg-blue-700 transition-all inline-flex items-center gap-1.5"
                    >
                      <span className="material-symbols-outlined text-[14px]">refresh</span>
                      Retry
                    </button>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400 text-xs text-slate-500">
                    <span className="material-symbols-outlined text-[36px] text-slate-300 block mb-2">face_co</span>
                    No cockpit administrator accounts match your criteria.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50/40 text-xs text-slate-700 transition-colors">
                    <td className="py-4 px-4 font-bold text-slate-900 flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200/60 flex items-center justify-center font-bold text-[#136bce] uppercase text-[11px]">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="truncate max-w-[150px]">{user.name}</span>
                    </td>
                    <td className="py-4 px-4 font-semibold text-slate-600 font-mono">
                      {user.email}
                    </td>
                    <td className="py-4 px-4 text-slate-500 font-medium">
                      {user.createdOn}
                    </td>
                    <td className="py-4 px-4 text-slate-500 font-medium font-mono">
                      {user.lastLoggedIn}
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => toggleUserStatus(user)}
                        title="Click to toggle status"
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border transition-all ${
                          user.status === 'Active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100/50'
                            : 'bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-100/50'
                        }`}
                      >
                        <span className={`w-1 h-1 rounded-full ${user.status === 'Active' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {user.status}
                      </button>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="p-1.5 h-8 w-8 text-[#136bce] hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100 flex items-center justify-center"
                          title="Edit admin account"
                        >
                          <span className="material-symbols-outlined text-[16px]">edit</span>
                        </button>
                        <button
                          onClick={() => deleteUser(user)}
                          className={`p-1.5 h-8 w-8 text-slate-450 hover:text-red-650 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100 flex items-center justify-center`}
                          title="Remove admin"
                        >
                          <span className="material-symbols-outlined text-[16px]">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADD / EDIT COCKPIT USER MODAL */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl border border-slate-200 w-full max-w-lg p-6 shadow-2xl flex flex-col gap-5 overflow-y-auto max-h-[90dvh]"
            >
              <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">
                    {isEditing ? 'edit_square' : 'person_add'}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900">
                    {isEditing ? 'Edit Cockpit Admin' : 'Add New Admin'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="flex flex-col gap-5 text-left">
                {/* SECTION 1: ACCOUNT PROFILE */}
                <div className="flex flex-col gap-3.5">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">badge</span>
                    Admin Account Profile
                  </span>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Full Name *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Liam Anderson"
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Email ID *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. liam.anderson@qamate.ai"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    />
                  </div>
                </div>

                {/* SECTION 2: PASSWORDS */}
                <div className="flex flex-col gap-3.5 bg-slate-50 rounded-2xl p-4 border border-slate-200/65">
                  <div className="flex items-center justify-between border-b border-slate-200/50 pb-1.5">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[14px] text-[#136bce]">vpn_key</span>
                      Passphrase & Credentials
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyCredentials}
                      className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-white rounded-lg text-[#136bce] hover:bg-slate-100 border border-slate-200 hover:border-slate-300 transition-all shadow-sm"
                      title="Copy credentials to clipboard"
                    >
                      <span className="material-symbols-outlined text-[13px]">{copySuccess ? 'check' : 'content_copy'}</span>
                      {copySuccess ? 'Copied' : 'Copy Credentials'}
                    </button>
                  </div>

                  {isEditing && (
                    <p className="text-[10px] text-slate-400 font-medium -mt-1">Leave blank to keep the current password.</p>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">{isEditing ? 'New Password' : 'Password *'}</label>
                      <input
                        required={!isEditing}
                        className="w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-mono"
                        placeholder="••••••••••••"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Confirm Password {isEditing ? '' : '*'}</label>
                      <input
                        required={!isEditing}
                        className="w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-mono"
                        placeholder="••••••••••••"
                        type="password"
                        value={newUser.confirmPassword}
                        onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 3: ACCESS STATE */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">shield_person</span>
                    Authorizations & Status
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Account Status</label>
                      <select
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs text-slate-800 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none cursor-pointer"
                        value={newUser.status}
                        onChange={(e) => setNewUser({ ...newUser, status: e.target.value as any })}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">System Clearance</label>
                      <input
                        disabled
                        className="w-full h-10 rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-2 text-xs text-slate-400 focus:outline-none cursor-not-allowed font-semibold"
                        value="Master Admin Access"
                        type="text"
                      />
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20 disabled:opacity-60 disabled:pointer-events-none inline-flex items-center gap-2"
                  >
                    {isSubmitting && <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>}
                    {isEditing ? 'Save Details' : 'Add Admin'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
